import { useState } from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import { Box, Grid, Button, TextField, IconButton, InputAdornment, Typography } from '@mui/material'
import { RemoveRedEyeOutlined as IRemoveRedEye, VisibilityOffOutlined as IVisibilityOff } from '@mui/icons-material'
import { env } from '../../core/Env'
import { rqx } from '../../core/request/API'
import { continueAfterAuth } from '../pwaShell'
import { pslstyle } from '../../core/styling/si'
import { validateEmail, validatePassword } from '../../core/validator/Validator'
import { SnakeLoader } from '../../core/loader/SnakeLoader'
import { grantToolAccess } from './AddAccess'
import LPFSS from '../../assets/images/logos/pofsis_no_border.png'
import GSPFSS from '../../assets/images/logos/pofsis_grayscale.jpg'
import LPSL from '../../assets/images/logos/psl_logo.png'
import LBIZ from '../../assets/images/logos/biz_logo.png'

const parseLogo = (logo) => {
    if (!logo) return null
    try {
        return typeof logo === 'string' ? JSON.parse(logo) : logo
    } catch (err) {
        return null
    }
}

const defaultToolId = (platform) =>
    platform === 'psl'
        ? (env() === 'prod' ? 102 : 18)
        : platform === 'biz'
            ? (env() === 'prod' ? 103 : 13)
            : 0

/**
 * Plug-and-play registration for EntryAccess:
 * - email: su/attempt/email
 * - personal mobile: su/attempt/mobile_num
 * - biz mobile (useBizMobileFlow): entryaccess_dual (email + mobile) then grant
 */
export const RegisterAccess = (props) => (
    <Router>
        <RegisterAccessForm {...props} />
    </Router>
)

const RegisterAccessForm = ({
    email,
    account: accountProp,
    mobile,
    va = 'email',
    useBizMobileFlow = false,
    platform,
    apiPlatform,
    platformQuery,
    tool,
    authCallback,
    from,
    subcb,
    pricing,
    ref_a_frnd,
}) => {
    const attemptPlatform = apiPlatform || platform
    const account = accountProp || email || mobile
    const isDual = useBizMobileFlow && va === 'mobile_num'
    const isMobileOnly = !isDual && va === 'mobile_num'

    const classes = pslstyle(attemptPlatform)
    const toolName = tool?.name || 'this application'
    const toolLogo = parseLogo(tool?.logo) || (attemptPlatform === 'psl' ? LPSL : LBIZ)

    const [bizEmail, setBizEmail] = useState('')
    const [password, setPassword] = useState('')
    const [repass, setRepass] = useState('')
    const [showPass, setShowPass] = useState(false)
    const [showRepass, setShowRepass] = useState(false)
    const [validated, setValidated] = useState({ lc: false, uc: false, number: false, length: false, sc: false })
    const [passBusy, setPassBusy] = useState(false)
    const [passError, setPassError] = useState('')

    const persistSession = () => {
        const tinfo = tool
            ? [{ name: tool.name, tid: tool.tid, type: tool.type, logo: tool.logo }]
            : [{ name: 'Tool', tid: null, type: 1, logo: null }]
        const strg = {
            cb: authCallback,
            tid: tool?.tid ?? null,
            tinfo,
            expiration: '',
            platform: platformQuery || (attemptPlatform === 'biz' ? 'business' : 'personal'),
            tsa_key: from,
            pricing,
            subcb,
            ref_a_frnd,
        }
        localStorage.setItem('pm_tool_session', JSON.stringify(strg))
        return strg
    }

    const onPasswordChange = (value, which) => {
        if (which === 'password') {
            setPassword(value)
            const vp = validatePassword(value)
            setValidated(vp.validated)
        } else {
            setRepass(value)
        }
        setPassError('')
    }

    const submitPassword = async () => {
        if (passBusy) return
        setPassError('')

        if (isDual) {
            if (!bizEmail || !validateEmail(bizEmail)) {
                setPassError('A valid business email is required')
                return
            }
        }
        if (!password || !repass) {
            setPassError('Password is required')
            return
        }
        if (password !== repass) {
            setPassError('Passwords do not match')
            return
        }
        const vp = validatePassword(password)
        if (vp.wvalidator !== 0) {
            setPassError('Password does not meet the requirements')
            return
        }
        if (!authCallback) {
            setPassError('Callback URL is missing')
            return
        }

        setPassBusy(true)
        try {
            const toolId = tool?.tid || defaultToolId(attemptPlatform)
            let attempt

            if (isDual) {
                const emailValue = bizEmail.trim()

                // Same as LinkEmailAccess: SI email ref 1 — "valid" means already taken
                const check = await rqx.get(
                    `<<tool>>/${env()}/${attemptPlatform}/si/attempt/email`,
                    { account: emailValue, ref: 1, platform: attemptPlatform }
                )
                if (check?.msg === 'valid') {
                    setPassError('This email is already registered to another account.')
                    setPassBusy(false)
                    return
                }

                const body = {
                    email: emailValue,
                    mobile_num: mobile || account,
                    password,
                    ref: '2',
                    parent_id: 0,
                    tool_id: toolId,
                }
                attempt = await rqx.post(
                    `<<tool>>/${env()}/biz/su/attempt/entryaccess_dual`,
                    body
                )
                if (attempt?.msg === 'email_exists') {
                    setPassError('This email is already registered.')
                    setPassBusy(false)
                    return
                }
                if (attempt?.msg === 'mobile_exists') {
                    setPassError('This mobile number is already registered.')
                    setPassBusy(false)
                    return
                }
            } else if (isMobileOnly) {
                const body = {
                    account: mobile || account,
                    password,
                    ref: '2',
                    parent_id: 0,
                    tool_id: toolId,
                }
                attempt = await rqx.post(
                    `<<tool>>/${env()}/${attemptPlatform}/su/attempt/mobile_num`,
                    body
                )
            } else {
                const body = {
                    account: email || account,
                    password,
                    ref: '2',
                    parent_id: 0,
                    tool_id: toolId,
                }
                attempt = await rqx.post(
                    `<<tool>>/${env()}/${attemptPlatform}/su/attempt/email`,
                    body
                )
            }

            if (!attempt?.ainfo) {
                setPassError(attempt?.msg || 'Unable to continue. Please try again.')
                setPassBusy(false)
                return
            }

            const grant = await grantToolAccess({
                ainfo: attempt.ainfo,
                platform: attemptPlatform,
                tid: tool?.tid || toolId,
            })

            if (grant?.msg !== 'success') {
                setPassError(grant?.msg || 'Something went wrong. Please try again.')
                setPassBusy(false)
                return
            }

            persistSession()
            const params = new URLSearchParams()
            params.set('ainfo', JSON.stringify(attempt.ainfo))
            if (subcb) params.set('subcb', subcb)
            params.set('ref', 'auth')
            continueAfterAuth(`${authCallback}?${params.toString()}`, { platform: attemptPlatform })
        } catch (err) {
            setPassError(`request failed: ${err?.message || err}`)
            setPassBusy(false)
        }
    }

    const displayCredential = isDual || isMobileOnly
        ? `+63 ${mobile || account}`
        : (email || account)

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="center" height="100%" minHeight="100vh">
            <Box borderRadius={5} p={3}>
                <Box
                    boxShadow="0px 0px 4px rgba(0, 0, 0, 0.04), 0px 8px 16px rgba(0, 0, 0, 0.08)"
                    bgcolor="#FFF"
                    borderRadius="8px"
                    px={4}
                    py={5}
                    width="auto"
                    height="auto"
                    minWidth="480px"
                >
                    <Grid container spacing={3}>
                        <Grid container flexDirection="column" item xs={12} alignItems="center">
                            <Box display="flex" justifyContent="center" alignItems="center" width="100%" mb={1}>
                                <Box p={1} display="flex" alignItems="center">
                                    <img src={LPFSS} alt="POFSIS Logo" width="52" />
                                </Box>
                                <Box className="personal-logo-px-1" ml={3} sx={{ backgroundImage: `url(${toolLogo})` }} />
                            </Box>

                            <Box mt={3} fontSize="30px" fontWeight="600" align="center" color="#101828" width="100%">
                                {isDual ? 'Create your account' : 'Verify it’s you'}
                            </Box>
                            <Box mt={1.5} fontSize="16px" fontWeight="400" align="center" color="#5B6167" width="100%">
                                {isDual
                                    ? `Add your business email and set a password to continue to ${toolName}.`
                                    : `For your security, re-enter your password to finish and continue to ${toolName}.`}
                            </Box>
                            <Box mt={2} fontSize={14} fontWeight="600" color={attemptPlatform === 'psl' ? 'primary.personal' : 'primary.business'}>
                                {displayCredential}
                            </Box>

                            <Box mt={4} width="100%" fontSize="14px" fontWeight="400" color="#292C2F" textAlign="left">
                                <Box
                                    component="form"
                                    autoComplete="off"
                                    onSubmit={(e) => {
                                        e.preventDefault()
                                        submitPassword()
                                    }}
                                >
                                    {isDual && (
                                        <>
                                            <Box display="flex">
                                                Business email
                                                <Box color="red" ml={0.5} mb={1}>*</Box>
                                            </Box>
                                            <TextField
                                                size="small"
                                                variant="outlined"
                                                type="text"
                                                value={bizEmail}
                                                disabled={passBusy}
                                                fullWidth
                                                className={`${classes.tf} ${classes.border}`}
                                                onChange={(e) => {
                                                    setBizEmail(e.target.value)
                                                    setPassError('')
                                                }}
                                                inputProps={{ autoComplete: 'off' }}
                                                sx={{ mb: 2 }}
                                            />
                                        </>
                                    )}

                                    <Box display="flex">
                                        Password
                                        <Box color="red" ml={0.5} mb={1}>*</Box>
                                    </Box>
                                    <Box fontSize={13} color="#5B6167" mb={1}>This extra step helps protect your account.</Box>
                                    <TextField
                                        size="small"
                                        variant="outlined"
                                        type={showPass ? 'text' : 'password'}
                                        value={password}
                                        disabled={passBusy}
                                        fullWidth
                                        className={`${classes.tf} ${classes.border}`}
                                        onChange={(e) => onPasswordChange(e.target.value, 'password')}
                                        inputProps={{ autoComplete: 'new-password' }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPass((v) => !v)}>
                                                        {showPass ? <IRemoveRedEye /> : <IVisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Box display="flex" mt={2}>
                                        Re-enter password
                                        <Box color="red" ml={0.5} mb={1}>*</Box>
                                    </Box>
                                    <TextField
                                        size="small"
                                        variant="outlined"
                                        type={showRepass ? 'text' : 'password'}
                                        value={repass}
                                        disabled={passBusy}
                                        error={Boolean(passError)}
                                        fullWidth
                                        className={`${classes.tf} ${classes.border}`}
                                        onChange={(e) => onPasswordChange(e.target.value, 'repass')}
                                        inputProps={{ autoComplete: 'new-password' }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowRepass((v) => !v)}>
                                                        {showRepass ? <IRemoveRedEye /> : <IVisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Box mt={1.5} color="#6A6A6A" fontSize="12px">
                                        <Typography variant="body2" component="div" style={{ fontSize: '12px' }}>
                                            {!validated.length && 'Minimum of 8 characters, '}
                                            {!validated.lc && 'Lower case, '}
                                            {!validated.uc && 'Upper case, '}
                                            {!validated.number && 'Number (123), '}
                                            {!validated.sc && 'Symbol (e.g., @*#-)'}
                                        </Typography>
                                    </Box>

                                    <Box color="error.main" fontSize={13} mt={1} minHeight="20px">{passError}</Box>

                                    <Button
                                        type="submit"
                                        size="large"
                                        variant="contained"
                                        className={classes.button}
                                        fullWidth
                                        disabled={passBusy}
                                        sx={{ marginTop: '16px', marginBottom: '8px' }}
                                    >
                                        {passBusy ? (
                                            <Box display="flex" alignItems="center" p={0.9}>
                                                <SnakeLoader bg="#ffffff" size="0.75rem" distance="0.3rem" />
                                            </Box>
                                        ) : (
                                            'Continue'
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Box display="flex" alignItems="center">
                    <img src={GSPFSS} alt="POFSIS" width="14" />
                    <Box fontSize={16} fontWeight="600" color="#333333" ml={1}>POFSIS</Box>
                </Box>
                <Box color="#5B6167" fontSize="12px" mb={3}>One account for Personal , Business and more.</Box>
            </Box>
        </Box>
    )
}
